/*
 eslint-disable  @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument
 */

import type {LogMethods} from '@significo/pulse';
import type {
  DeleteParams,
  GetParams,
  ParamsCommon,
  PostMultipartParams,
  PostParams,
  PutParams,
} from '@/ts/types/component/http-wrapper.type';
import {
  AppErrorCode,
  type ErrorCode,
  errorCodeMapping,
  ErrorCodesDto,
  isExpectedErrorCode,
} from '@/ts/types/dto/error-codes-dto';
import {AppError} from '@/ts/utils/errors';
import {sentryService} from '@/ts/services/sentry-service';

type HttpMethod = 'DELETE' | 'GET' | 'POST' | 'PUT';

/** Wraps the native fetch API with logging, global headers, error handling, and automatic 401 redirect */
export class FetchWrapper {
  private static readonly httpAccessDenied = 401;
  private static readonly httpNoContent = 204;
  private static readonly httpNotFound = 404;
  private static readonly httpOK = 200;
  private static readonly httpLast = 299;

  // eslint-disable-next-line @typescript-eslint/max-params -- 5th param is optional token refresh callback
  constructor(
    public readonly backendUrl: string,
    private readonly getGlobalHeaders: () => Promise<Record<string, string>> | Record<string, string>,
    private readonly onAccessDenied: () => Promise<void>,
    private readonly logger: LogMethods,
    private readonly onTokenRefresh?: () => Promise<Record<string, string> | null>,
  ) {
  }

  public async get<T>(data: GetParams): Promise<T> {
    return this.makeRequest<T>(
      'GET',
      null,
      data,
    );
  }

  public async getBlob(data: GetParams): Promise<{blob: Blob; filename: string}> {
    const urlParams = data.queryParams ? `?${new URLSearchParams(data.queryParams).toString()}` : '';
    const formattedUrl = `${this.backendUrl}${data.url}${urlParams}`;

    this.logger.debug(`Fetch GET (blob):${formattedUrl}`)();

    sentryService.addBreadcrumb({
      category: 'http',
      message: `GET (blob) ${data.url}`,
      level: 'info',
      data: {method: 'GET', url: formattedUrl},
    });

    let result: Response;
    try {
      result = await fetch(formattedUrl, {
        method: 'GET',
        mode: 'cors',
        headers: await this.getAllHeaders(data.headers),
        redirect: 'manual',
      });
    } catch (err: unknown) {
      this.logger.error(`error happened during fetching ${formattedUrl}`, err)();
      throw this.reportRequestFailure(err, 'GET', formattedUrl, data.url);
    }

    if (result.status === FetchWrapper.httpAccessDenied) {
      result = await this.handleBlobTokenRefresh(result, formattedUrl, data);
    }

    if (!result.ok) {
      const apiError = new AppError(`Failed GET:${data.url} status ${result.status}`, ErrorCodesDto.UNKNOWN);
      sentryService.captureException(apiError, {
        method: 'GET',
        url: formattedUrl,
        status: result.status,
      });
      throw apiError;
    }

    const disposition = result.headers.get('Content-Disposition') ?? '';
    const filename = /filename="?(?<name>[^";\n]+)"?/u.exec(disposition)?.groups?.name ?? 'report.xlsx';

    const blob = await result.blob();
    return {blob, filename};
  }

  public async delete<T>(data: DeleteParams): Promise<T> {
    return this.makeRequest<T>(
      'DELETE',
      null,
      data,
    );
  }

  public async post<T>(data: PostParams): Promise<T> {
    return this.makeRequest<T>(
      'POST',
      JSON.stringify(data.body),
      data,
    );
  }

  public async postUrlEncoded<T>(data: PostParams<Record<string, string>>): Promise<T> {
    return this.makeRequest<T>(
      'POST',
      new URLSearchParams(data.body!).toString(),
      data,
    );
  }

  public async postMultiData<T>(data: PostMultipartParams): Promise<T> {
    return this.makeRequest<T>(
      'POST',
      data.body,
      data,
    );
  }

  public async put<T>(data: PutParams): Promise<T> {
    return this.makeRequest<T>(
      'PUT',
      JSON.stringify(data.body),
      data,
    );
  }

  private async makeRequest<T>(
    method: HttpMethod,
    body: BodyInit | null,
    data: ParamsCommon,
    options: {isRetry?: boolean} = {},
  ): Promise<T> {
    let urlParams = '';
    if (data.queryParams) {
      urlParams = `?${new URLSearchParams(data.queryParams).toString()}`;
    }
    this.logger.debug(
      `Fetch ${method}:${this.backendUrl}${data.url}`,
      ...(data.queryParams ? ['urlSearch:', data.queryParams] : []),
      ...(body ? ['body:', body] : []),
    )();
    const formattedUrl = `${this.backendUrl}${data.url}${urlParams}`;

    // Add Sentry breadcrumb for request start
    const interventionId = sentryService.extractInterventionIdFromPath(data.url);
    sentryService.addBreadcrumb({
      category: 'http',
      message: `${method} ${data.url}`,
      level: 'info',
      data: {
        method,
        url: formattedUrl,
        ...(interventionId && {interventionId}),
      },
    });

    // Set HTTP context for potential error capture (snake_case matches backend)
    sentryService.setHttpContext({
      method,
      path: data.url,
      intervention_id: interventionId, // eslint-disable-line @typescript-eslint/naming-convention
    });

    let result: Response;
    try {
      result = await fetch(formattedUrl, {
        method,
        mode: 'cors',
        headers: await this.getAllHeaders(data.headers),
        redirect: 'manual', // avoid zitadel redirect, and we don't use reditect on backend or anything else
        body,
      });
    } catch (err: unknown) {
      this.logger.error(`error happened during fetching ${formattedUrl}`, err)();
      throw this.reportRequestFailure(err, method, formattedUrl, data.url);
    }

    // Add response breadcrumb
    sentryService.addBreadcrumb({
      category: 'http',
      message: `Response: ${method} ${data.url} - ${result.status}`,
      level: result.ok ? 'info' : 'warning',
      data: {status: result.status, statusText: result.statusText},
    });

    if (result.status === FetchWrapper.httpAccessDenied) {
      if (!options.isRetry && this.onTokenRefresh) {
        this.logger.debug('Got 401, attempting token refresh and retry')();
        const refreshedHeaders = await this.onTokenRefresh();
        if (refreshedHeaders) {
          return this.makeRequest<T>(
            method,
            body,
            {...data, headers: refreshedHeaders},
            {isRetry: true},
          );
        }
      }
      return this.rejectUnauthorized(method, data);
    }

    const resultBody = await result.text();
    return this.handleResponse<T>(result, data, resultBody, method);
  }

  /**
   * Reports whatever aborted the fetch and returns the error to throw.
   * getAllHeaders is awaited inside makeRequest's try, so an AppError raised while building the
   * headers (SESSION_EXPIRED when there is no token) lands in the same catch as a genuine network
   * failure. Keep its own code instead of relabelling it: Sentry should not call it a network error.
   */
  private reportRequestFailure(
    err: unknown,
    method: HttpMethod,
    formattedUrl: string,
    url: string,
  ): AppError {
    if (err instanceof AppError) {
      sentryService.captureException(err, {method, url: formattedUrl});
      return err;
    }

    // A request the browser cancelled because the user navigated away is not a fault. Give it its
    // own code so the central filter drops it wherever it surfaces, rather than counting it as a
    // network failure.
    if (err instanceof Error && err.name === 'AbortError') {
      return new AppError(`${method}:${url} aborted`, AppErrorCode.REQUEST_ABORTED);
    }

    const networkError = new AppError(`${method}:${url} ${(err as any)?.message}`, AppErrorCode.NETWORK_ERROR);
    sentryService.captureException(networkError, {
      method,
      url: formattedUrl,
      originalError: (err as any)?.message,
    });
    sentryService.addBreadcrumb({
      category: 'http',
      message: `Network error: ${method} ${url}`,
      level: 'error',
      data: {error: (err as any)?.message},
    });
    return networkError;
  }

  /**
   * Turns an error response body into the AppError to reject with.
   * Two dialects reach here: the backend's `errorCode`, and the OAuth `error`/`error_description`
   * that Zitadel's token endpoint answers with. Translating the second is what stops a spent
   * refresh token arriving as `Unknown err ... : undefined, message: undefined`.
   */
  private static buildApiError(
    response: any,
    method: HttpMethod,
    url: string,
  ): {apiError: AppError; mappedErrorCode: string; backendErrorCode: string} {
    const oauthError = response?.error as string | undefined;
    const backendErrorCode = (response?.errorCode ?? oauthError) as string;
    const mappedErrorCode = (errorCodeMapping[backendErrorCode] ?? backendErrorCode) as string;
    const isKnownCode = Object.values(ErrorCodesDto).includes(mappedErrorCode as ErrorCodesDto) ||
      Object.values(AppErrorCode).includes(mappedErrorCode as AppErrorCode);

    if (oauthError) {
      // Name the OAuth error whichever branch it falls in: the ones that keep alerting become
      // diagnosable instead of arriving as `undefined`.
      const code = isKnownCode ? mappedErrorCode as ErrorCode : ErrorCodesDto.UNKNOWN;
      const apiError = new AppError(`${method}:${url} ${oauthError}: ${response?.error_description ?? ''}`, code);
      return {apiError, mappedErrorCode, backendErrorCode};
    }

    if (isKnownCode) {
      return {
        apiError: new AppError(response.message ?? `${method}:${url}`, mappedErrorCode as ErrorCode),
        mappedErrorCode,
        backendErrorCode,
      };
    }

    return {
      apiError: new AppError(`Unknown err ${method}:${url} : ${backendErrorCode}, message: ${response?.message}`,
        ErrorCodesDto.UNKNOWN),
      mappedErrorCode,
      backendErrorCode,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  private handleResponse<T>(result: Response, data: ParamsCommon, resultBody: string, method: HttpMethod): T {
    const isOk = result.status >= FetchWrapper.httpOK && result.status <= FetchWrapper.httpLast;
    if (result.status === FetchWrapper.httpNoContent || (data.emptyResponse && isOk)) {
      return null as any as T;
    }
    if (result.status === FetchWrapper.httpNotFound && data.handle404) {
      return null as any as T;
    }
    let response: any;
    try {
      if (resultBody) {
        response = JSON.parse(resultBody) as T;
      }
    } catch (_error) {
      const parseError = new AppError(`Invalid response from ${method}:${data.url}`, ErrorCodesDto.UNKNOWN);
      sentryService.captureException(parseError, {
        method,
        url: data.url,
        status: result.status,
      });
      throw parseError;
    }

    if (result.ok) {
      return response as T;
    }

    // Zitadel's token endpoint answers in OAuth shape ({error, error_description}) instead of the
    // backend's errorCode. Without translating it, a spent refresh token — the single largest
    // source of events in production — arrives as `Unknown err ... : undefined, message: undefined`,
    // which neither alerts usefully nor explains anything.
    const {apiError, mappedErrorCode, backendErrorCode} = FetchWrapper.buildApiError(response, method, data.url);

    // Open a Sentry issue for faults only. Expected outcomes still reject, so callers behave
    // exactly as before; they just stop alerting on flows that work as designed. Leave a breadcrumb
    // naming the code instead: makeRequest's response breadcrumb is written before the body is read
    // and carries only the status, so without this the timeline of a later issue would show a bare
    // 403 without saying which rule produced it.
    if (isExpectedErrorCode(mappedErrorCode)) {
      sentryService.addBreadcrumb({
        category: 'http',
        message: `Expected outcome: ${method} ${data.url} - ${mappedErrorCode}`,
        level: 'info',
        data: {status: result.status, errorCode: backendErrorCode},
      });
    }

    // Called unconditionally: whether this opens an issue is captureException's decision, so the
    // rule lives in one place. The branch above only decides whether to leave a breadcrumb.
    sentryService.captureException(apiError, {
      method,
      url: data.url,
      status: result.status,
      errorCode: backendErrorCode,
      responseMessage: response?.message,
    });

    throw apiError;
  }

  /**
   * Ends the session and rejects, unless the call asked to keep it: a request the reader did not
   * make must not be what throws them out of the page they are using.
   */
  private async rejectUnauthorized(method: string, data: ParamsCommon): Promise<never> {
    if (!data.keepSessionOn401) {
      await this.onAccessDenied();
    }
    throw new AppError(`401 for ${method}:${data.url} (after refresh)`, ErrorCodesDto.UNAUTHORIZED);
  }

  private async handleBlobTokenRefresh(result: Response, formattedUrl: string, data: GetParams): Promise<Response> {
    if (!this.onTokenRefresh) {
      await this.onAccessDenied();
      throw new AppError(`401 for GET:${data.url} (blob, no refresh)`, ErrorCodesDto.UNAUTHORIZED);
    }
    this.logger.debug('Got 401 on blob request, attempting token refresh and retry')();
    const refreshedHeaders = await this.onTokenRefresh();
    if (!refreshedHeaders) {
      await this.onAccessDenied();
      throw new AppError(`401 for GET:${data.url} (blob, refresh failed)`, ErrorCodesDto.UNAUTHORIZED);
    }
    const retryResult = await fetch(formattedUrl, {
      method: 'GET',
      mode: 'cors',
      headers: {...refreshedHeaders, ...data.headers},
      redirect: 'manual',
    });
    if (retryResult.status === FetchWrapper.httpAccessDenied) {
      await this.onAccessDenied();
      throw new AppError(`401 for GET:${data.url} (blob, retry rejected)`, ErrorCodesDto.UNAUTHORIZED);
    }
    return retryResult;
  }

  private async getAllHeaders(originHeaders?: Record<string, string>): Promise<Record<string, string>> {
    return {
      'Content-Type': 'application/json', // eslint-disable-line @typescript-eslint/naming-convention
      ...originHeaders,
      ...(await this.getGlobalHeaders()),
    };
  }
}
